import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, startOfYear, endOfYear, subYears, startOfQuarter, endOfQuarter } from 'date-fns';
import authFetch from '../utils/api';
import './Financials.css';

const PAYMENT_LABELS = {
  cash: 'Cash',
  zelle: 'Zelle',
  cashapp: 'Cash App',
  venmo: 'Venmo',
  paypal: 'PayPal',
  apple_cash: 'Apple Cash',
  stripe: 'Stripe',
  tap_to_pay: 'Tap to Pay',
  comp: 'Comped / Giveaway',
  unknown: 'Unknown'
};

const PRESETS = [
  { id: 'thisYear', label: 'This Year' },
  { id: 'lastYear', label: 'Last Year' },
  { id: 'q1', label: 'Q1' },
  { id: 'q2', label: 'Q2' },
  { id: 'q3', label: 'Q3' },
  { id: 'q4', label: 'Q4' },
  { id: 'custom', label: 'Custom' }
];

const money = (n) => `$${(n || 0).toFixed(2)}`;

const Financials = () => {
  const [preset, setPreset] = useState('thisYear');
  const [year, setYear] = useState(new Date().getFullYear());
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getRange = useCallback(() => {
    const now = new Date();
    if (preset === 'thisYear') return { start: startOfYear(now), end: endOfYear(now) };
    if (preset === 'lastYear') {
      const d = subYears(now, 1);
      return { start: startOfYear(d), end: endOfYear(d) };
    }
    if (preset === 'q1' || preset === 'q2' || preset === 'q3' || preset === 'q4') {
      const monthStart = { q1: 0, q2: 3, q3: 6, q4: 9 }[preset];
      const anchor = new Date(year, monthStart, 1);
      return { start: startOfQuarter(anchor), end: endOfQuarter(anchor) };
    }
    if (preset === 'custom') {
      if (!customStart || !customEnd) return null;
      return { start: parseISO(customStart), end: parseISO(customEnd) };
    }
    return null;
  }, [preset, year, customStart, customEnd]);

  const fetchData = useCallback(async () => {
    const range = getRange();
    if (!range) return;

    setLoading(true);
    setError('');
    try {
      const start = format(range.start, 'yyyy-MM-dd');
      const end = format(range.end, 'yyyy-MM-dd');
      const res = await authFetch(`/api/financials/export?start=${start}&end=${end}`);
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || 'Failed to load financial data');
        setData(null);
        return;
      }
      setData(json);
    } catch (err) {
      console.error('Error fetching financials:', err);
      setError('Failed to load financial data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [getRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 5; i--) years.push(i);
    return years;
  };

  const handleExportCsv = () => {
    if (!data) return;

    const headers = ['Date', 'Invoice #', 'Customer', 'Payment Method', 'Service Revenue', 'Comp/Discount', 'Sales Tax', 'Tip', 'Total Collected', 'Stripe Fee', 'Net Deposited'];
    const rows = data.rows.map(r => [
      format(parseISO(r.date), 'MM/dd/yyyy'),
      r.invoiceNumber,
      `"${r.customerName}"`,
      PAYMENT_LABELS[r.paymentMethod || 'unknown'] || r.paymentMethod,
      r.serviceRevenue.toFixed(2),
      r.compDiscount.toFixed(2),
      r.taxAmount.toFixed(2),
      r.tip.toFixed(2),
      r.totalCollected.toFixed(2),
      r.stripeFee != null ? r.stripeFee.toFixed(2) : '',
      r.netDeposited.toFixed(2)
    ]);

    const totalsRow = [
      '', '', '', 'TOTALS',
      data.totals.serviceRevenue.toFixed(2),
      data.totals.compDiscount.toFixed(2),
      data.totals.taxAmount.toFixed(2),
      data.totals.tip.toFixed(2),
      data.totals.totalCollected.toFixed(2),
      data.totals.stripeFee.toFixed(2),
      data.totals.netDeposited.toFixed(2)
    ];

    const csv = [headers, ...rows, totalsRow].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `financials-${data.startDate}-to-${data.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="financials-page">
      <div className="page-header">
        <h1 className="page-header__title">Financials</h1>
        <div className="page-header__actions">
          <button className="btn btn--primary" onClick={handleExportCsv} disabled={!data || data.rows.length === 0}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="financials__presets">
        {PRESETS.map(p => (
          <button
            key={p.id}
            className={`financials__preset-btn ${preset === p.id ? 'financials__preset-btn--active' : ''}`}
            onClick={() => setPreset(p.id)}
          >
            {p.label}
          </button>
        ))}

        {(preset === 'q1' || preset === 'q2' || preset === 'q3' || preset === 'q4') && (
          <select className="form-select financials__year-select" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}>
            {getYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      </div>

      {preset === 'custom' && (
        <div className="financials__custom-range">
          <input type="date" className="form-input" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
          <span>to</span>
          <input type="date" className="form-input" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
        </div>
      )}

      {error && <div className="alert alert--error">{error}</div>}

      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      )}

      {!loading && data && (
        <>
          <div className="financials__summary-grid">
            <div className="financials__card financials__card--highlight">
              <div className="financials__card-label">Total Collected</div>
              <div className="financials__card-value">{money(data.totals.totalCollected)}</div>
            </div>
            <div className="financials__card">
              <div className="financials__card-label">Service Revenue</div>
              <div className="financials__card-value">{money(data.totals.serviceRevenue)}</div>
            </div>
            <div className="financials__card">
              <div className="financials__card-label">Sales Tax Collected</div>
              <div className="financials__card-value">{money(data.totals.taxAmount)}</div>
            </div>
            <div className="financials__card">
              <div className="financials__card-label">Tips</div>
              <div className="financials__card-value">{money(data.totals.tip)}</div>
            </div>
            <div className="financials__card">
              <div className="financials__card-label">Stripe Fees</div>
              <div className="financials__card-value">{money(data.totals.stripeFee)}</div>
            </div>
            <div className="financials__card">
              <div className="financials__card-label">Net Deposited</div>
              <div className="financials__card-value">{money(data.totals.netDeposited)}</div>
            </div>
            {data.totals.compDiscount > 0 && (
              <div className="financials__card">
                <div className="financials__card-label">Comp / Discount</div>
                <div className="financials__card-value">{money(data.totals.compDiscount)}</div>
              </div>
            )}
          </div>

          {Object.keys(data.byPaymentMethod).length > 0 && (
            <div className="financials__section">
              <h2>By Payment Method</h2>
              <div className="financials__method-grid">
                {Object.entries(data.byPaymentMethod).map(([method, info]) => (
                  <div key={method} className="financials__method-row">
                    <span className="financials__method-name">{PAYMENT_LABELS[method] || method}</span>
                    <span className="financials__method-count">{info.count} job{info.count !== 1 ? 's' : ''}</span>
                    <span className="financials__method-total">{money(info.totalCollected)}</span>
                  </div>
                ))}
                {(() => {
                  const methodTotals = Object.values(data.byPaymentMethod).reduce(
                    (acc, info) => ({ count: acc.count + info.count, totalCollected: acc.totalCollected + info.totalCollected }),
                    { count: 0, totalCollected: 0 }
                  );
                  return (
                    <div className="financials__method-row financials__method-row--total">
                      <span className="financials__method-name">Total</span>
                      <span className="financials__method-count">{methodTotals.count} job{methodTotals.count !== 1 ? 's' : ''}</span>
                      <span className="financials__method-total">{money(methodTotals.totalCollected)}</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          <div className="financials__section">
            <h2>Jobs ({data.rows.length})</h2>
            <div className="financials__table-wrap">
              <table className="financials__table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice #</th>
                    <th>Customer</th>
                    <th>Payment Method</th>
                    <th>Service Revenue</th>
                    <th>Comp/Discount</th>
                    <th>Sales Tax</th>
                    <th>Tip</th>
                    <th>Total Collected</th>
                    <th>Stripe Fee</th>
                    <th>Net Deposited</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 && (
                    <tr><td colSpan={11} className="financials__empty">No paid jobs in this date range.</td></tr>
                  )}
                  {data.rows.map(r => (
                    <tr key={r.invoiceNumber}>
                      <td>{format(parseISO(r.date), 'MM/dd/yyyy')}</td>
                      <td>{r.invoiceNumber}</td>
                      <td>{r.customerName}</td>
                      <td>{PAYMENT_LABELS[r.paymentMethod || 'unknown'] || r.paymentMethod}</td>
                      <td>{money(r.serviceRevenue)}</td>
                      <td>{r.compDiscount > 0 ? money(r.compDiscount) : '—'}</td>
                      <td>{money(r.taxAmount)}</td>
                      <td>{r.tip > 0 ? money(r.tip) : '—'}</td>
                      <td>{money(r.totalCollected)}</td>
                      <td>{r.stripeFee != null ? money(r.stripeFee) : '—'}</td>
                      <td>{money(r.netDeposited)}</td>
                    </tr>
                  ))}
                </tbody>
                {data.rows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={4}>TOTALS</td>
                      <td>{money(data.totals.serviceRevenue)}</td>
                      <td>{money(data.totals.compDiscount)}</td>
                      <td>{money(data.totals.taxAmount)}</td>
                      <td>{money(data.totals.tip)}</td>
                      <td>{money(data.totals.totalCollected)}</td>
                      <td>{money(data.totals.stripeFee)}</td>
                      <td>{money(data.totals.netDeposited)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Financials;
